import type { Shape } from './en';
import type { findingsDe } from './findings.de';

export const findingsEn: Shape<typeof findingsDe> = {
  title: 'Findings',
  summary: '{{open}} open of {{total}}',
  numbered: 'Finding {{number}}',
  numberedPositive: 'Positive finding {{number}}',
  cardName: 'Finding {{number}}: {{title}}',
  cardNamePositive: 'Positive finding {{number}}: {{title}}',
  add: 'Add finding',
  copy: 'Take over findings',
  more_one: 'Show {{count}} more',
  more_other: 'Show {{count}} more',
  empty: 'No findings yet',
  emptyHint: 'Take findings from the defect catalog or describe them freely.',
  emptyFiltered: 'No findings for these filters',
  emptyFilteredHint: 'Change the search or the filters, or reset the filters.',
  loadFailed: 'The findings could not be loaded.',
  unavailable: 'Not available offline',
  unavailableHint: 'The findings of this entry are not stored on this device.',
  offline: 'Offline, view only.',
  offlineHeld: 'Offline: changes stay on the device and are sent once there is a connection again.',
  views: { label: 'View', list: 'List', plan: 'Findings plan', map: 'Findings map' },
  filters: {
    search: 'Search findings',
    searchPlaceholder: 'Number, title, location …',
    status: 'Status',
    category: 'Category',
    priority: 'Priority',
    allStatuses: 'All statuses',
    open: 'All open',
    positive: 'Positive findings',
    noStatus: 'No status',
    allCategories: 'All categories',
    noCategory: 'No category',
    allPriorities: 'All priorities',
    noPriority: 'No priority',
  },
  overview: {
    label: 'Findings by status',
    noStatus: 'No status',
    positive: 'Positive',
    pending_one: '{{count}} waiting to be sent',
    pending_other: '{{count}} waiting to be sent',
  },
  card: {
    positions_one: '{{count}} location',
    positions_other: '{{count}} locations',
    photos_one: '{{count}} photo',
    photos_other: '{{count}} photos',
    due: 'Due {{date}}',
    overdue: 'Overdue, due {{date}}',
    resolved: 'Resolved on {{date}}',
    geo_one: 'GPS point',
    geo_other: '{{count}} GPS points',
    positive: 'Positive',
    responsible: 'Responsible: {{name}}',
    dueNote: 'Deadline: {{text}}',
  },
  start: {
    label: 'Start with a photo',
    hint: 'Optionally take or choose a photo first, then search the catalog for the defect.',
    chosen_one: 'The photo goes with the defect created now.',
    chosen_other: 'The {{count}} photos go with the defect created now.',
    alt: 'Photo {{index}} for the new defect',
    removeDescription: 'The photo will not be added.',
  },
  sheet: {
    pickAt: 'Finding at this spot',
    free: 'Describe finding freely',
    positive: 'Add positive finding',
    ai: 'Add finding with AI',
    back: 'Back to the defect catalog',
  },
  picker: {
    search: 'Search the defect catalog',
    searchPlaceholder: 'e.g. fire extinguisher, escape route, ASR A2.3',
    enterHint: 'Enter takes the first match.',
    group: 'Group',
    allGroups: 'All groups',
    noGroup: 'No group',
    category: 'Category',
    allCategories: 'All categories',
    noCategory: 'No category',
    filters: 'Filter by group and category',
    filtersActive_one: 'Filter by group and category, {{count}} filter active',
    filtersActive_other: 'Filter by group and category, {{count}} filters active',
    templates_one: '{{count}} template',
    templates_other: '{{count}} templates',
    free: 'Describe freely',
    freeShort: 'Freely',
    positive: 'Add positive finding',
    positiveShort: 'Positive',
    ai: 'Add with AI',
    aiShort: 'With AI',
    hits_one: '{{count}} match',
    hits_other: '{{count}} matches',
    frequent: 'Frequently used',
    usage_one: 'Used once',
    usage_other: 'Used {{count}} times',
    more_one: 'Show {{count}} more template',
    more_other: 'Show {{count}} more templates',
    details: 'Details',
    detailsOf: 'Details of “{{title}}”',
    take: 'Use template',
    loadFailed: 'The defect catalog could not be loaded.',
    noCatalog: 'No defect catalog yet',
    noCatalogHint:
      'Administrators maintain templates in the settings of the module. Until then, describe findings freely.',
    empty: 'No matching template',
    emptyHint: 'Change the search or the filters, or describe the finding freely.',
  },
  free: {
    hint: 'Everything else follows in the form.',
    required: 'Please enter a title.',
    create: 'Create finding',
    positiveHint:
      'Something done well. The report lists it apart from the defects and does not count it as a defect.',
    createPositive: 'Create positive finding',
  },
  toCatalog: {
    action: 'Add to defect catalog',
    offline: 'The defect catalog can only be extended with a connection.',
  },
  ai: {
    improve: 'Improve with AI',
    improveFailed: 'No proposal could be created. Please try again.',
    discard: 'Discard',
    keywords: 'Keywords',
    keywordsHint: 'No names or other details about people.',
    keywordsPlaceholder: 'e.g. fire door 2nd floor wedged open, closer missing',
    required: 'Please enter keywords.',
    draft: 'Create proposal',
    positive: 'Positive finding',
    positiveHint: 'The AI describes what is done well instead of a defect.',
    failed: 'No proposal could be created. Please try again or describe the finding freely.',
    offline: 'AI is only available with a connection.',
    disclaimer: 'Please check the proposal. Once taken over, it can be changed in the form.',
    again: 'Change keywords',
    take: 'Take over',
    references: 'Legal references for the legal basis',
    quote: '“{{quote}}”',
    quoteUnverified: 'Quote not in the wording',
  },
  form: {
    title: 'Title',
    titleRequired: 'The title must not be empty.',
    category: 'Category',
    priority: 'Priority',
    status: 'Status',
    description: 'Description',
    measure: 'Measure',
    legalBasis: 'Legal basis',
    legalBasisPlaceholder: 'e.g. ASR A2.3 no. 5',
    location: 'Location',
    floor: 'Floor',
    area: 'Area',
    room: 'Room',
    dates: 'Dates',
    positive: 'Positive finding',
    positiveHint: 'Not a defect: the report lists it apart and does not count it as a defect.',
    responsible: 'Responsible',
    responsibleHint: 'Person or company who remedies the defect.',
    responsiblePlaceholder: 'Choose or type a person or company',
    responsibleSearch: 'Search or type',
    responsibleUse: 'Use “{{name}}”',
    responsibleEmpty: 'No suggestion. Type a name.',
    foundOn: 'Found on',
    dueOn: 'Due by',
    dueNote: 'Deadline as text',
    dueNotePlaceholder: 'e.g. daily before work starts',
    dueNoteHint: 'For deadlines without a fixed day, also in addition to the date.',
    dueSuggested: 'Suggested from the standard period of the priority.',
    resolvedOn: 'Resolved on',
    template: 'From the template “{{name}}”',
    source: 'Taken over from an earlier entry',
    done: 'Done',
    remove: 'Delete finding',
    removeTitle: 'Delete finding {{number}}?',
    removeDescription: 'The finding is deleted with its photos.',
    removeConfirm: 'Delete',
    removed: 'Finding {{number}} deleted',
  },
  positions: {
    title: 'Locations on plans',
    empty: 'No location set yet.',
    noPlans: 'There are no plans for this entry.',
    unknownPlan: 'Plan not available',
    page: 'Page {{page}}',
    add: 'Set location',
    change: 'Change',
    changeLabel: 'Change location {{index}}',
    remove: 'Remove location {{index}}',
  },
  photos: {
    title: 'Photos',
    empty: 'No photos',
    hint: 'Up to {{max}} photos. Large images are scaled down before the upload.',
    take: 'Take photo',
    choose: 'Choose photos',
    adding_one: 'Preparing {{count}} photo …',
    adding_other: 'Preparing {{count}} photos …',
    uploading: 'Uploading',
    waiting: 'Waiting for the upload',
    refused: 'Refused: {{reason}}',
    alt: 'Photo {{index}} of finding {{number}}',
    open: 'Enlarge photo {{index}}',
    moveUp: 'Move photo {{index}} forward',
    moveDown: 'Move photo {{index}} back',
    remove: 'Remove photo {{index}}',
    caption: 'Caption of photo {{index}}',
    captionPlaceholder: 'Caption',
    removeTitle: 'Remove photo?',
    removeDescription: 'The photo is removed from the finding and deleted.',
    removeConfirm: 'Remove',
    viewer: {
      previous: 'Previous photo',
      next: 'Next photo',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      position: 'Photo {{index}} of {{count}}',
    },
  },
  plan: {
    plan: 'Plan',
    hint: 'Tap a free spot to add a finding there. A marker opens its findings.',
    hintReadOnly: 'A marker opens its findings.',
    marker_one: 'Finding {{labels}}',
    marker_other: 'Findings {{labels}}',
    none: 'No plans',
    noneHint: 'This entry has no plans yet, or they are not on this device.',
    unplaced_one: '{{count}} finding has no location on a plan.',
    unplaced_other: '{{count}} findings have no location on a plan.',
    choose: 'Choose finding',
  },
  map: {
    label: 'Findings map',
    hint: 'Tap a free spot to add a finding there. A marker opens its findings.',
    hintReadOnly: 'A marker opens its findings.',
    unplaced_one: '{{count}} finding has no GPS point.',
    unplaced_other: '{{count}} findings have no GPS point.',
    fromPhotos: {
      action: 'Take GPS from photos',
      originals: 'Choose originals',
      hint: 'Every photo with a location becomes a GPS point of its finding; points closer than {{meters}} m count as one. Older uploads no longer have a location: choose the original photos from the gallery instead, the app finds them by file name.',
      offline: 'Taking GPS from photos needs a connection.',
      done_one: '{{count}} GPS point added.',
      done_other: '{{count}} GPS points added.',
      none: 'No new GPS points.',
      missing_one: '{{count}} photo has no location.',
      missing_other: '{{count}} photos have no location.',
      unmatched_one: '{{count}} photo matches no finding.',
      unmatched_other: '{{count}} photos match no finding.',
    },
  },
  geo: {
    title: 'GPS points',
    hint: 'Every photo with a location sets a point; points closer than {{meters}} m count as one. Otherwise use the location of the device, tap the map or paste coordinates.',
    empty: 'No GPS point yet.',
    point: 'Point {{index}}',
    change: 'Change',
    changeLabel: 'Change GPS point {{index}}',
    remove: 'Remove GPS point {{index}}',
    add: 'Set another point',
    adding: 'New GPS point',
    editing: 'GPS point {{index}}',
    sources: {
      photo: 'From the photo',
      device: 'Location of the device',
      map: 'Set by hand',
    },
    accuracy: '±{{meters}} m',
    fromPhoto_one: 'GPS point taken from the photo.',
    fromPhoto_other: '{{count}} GPS points taken from the photos.',
    fromDevice: 'The photo has no location, the GPS point comes from the device.',
  },
  copyDialog: {
    title: 'Take over findings',
    description:
      'Texts, category, priority, location, plan locations, GPS points and photos are taken over. Status and dates start anew.',
    source: 'From',
    from: 'From: {{source}}',
    findings: 'Findings',
    all: 'All',
    none: 'None',
    confirm_one: 'Take over {{count}} finding',
    confirm_other: 'Take over {{count}} findings',
    done_one: '{{count}} finding taken over',
    done_other: '{{count}} findings taken over',
    empty: 'This entry has no findings.',
    loadFailed: 'The findings of the earlier entry could not be loaded.',
    offline:
      'Not available without a connection: the findings of this earlier entry are not on this device.',
  },
  errors: {
    saveFailed: 'The change was not saved.',
    createFailed: 'The finding could not be created.',
    removeFailed: 'The finding could not be deleted.',
    copyFailed: 'The findings could not be taken over.',
    photoFailed: '“{{name}}” could not be saved as a photo.',
    'copy-same-record': 'Findings cannot be taken over into the same entry.',
    'finding-catalog-invalid': 'A chosen catalog entry is no longer available.',
    'finding-exists': 'This finding already exists.',
    'finding-photo-invalid': 'A photo does not belong to this finding.',
    'finding-plan-invalid': 'A location is on a plan that no longer exists.',
    'finding-too-large': 'The captions are too long in total.',
    'not-found': 'This finding no longer exists.',
    'offline-unavailable': 'This cannot be saved here without a connection.',
    'too-many-findings': 'An entry has at most {{max}} findings.',
    'too-many-photos': 'A finding has at most {{max}} photos.',
    'version-conflict': 'The finding was changed in the meantime. The current version is loading.',
  },
  catalogs: {
    statuses: {
      label: 'Statuses',
      description:
        'Statuses of findings in all modules. A status that counts as done removes a finding from the open ones.',
    },
    priorities: {
      label: 'Priorities',
      description:
        'Urgency of findings with a level (1 is the most urgent) and a standard period in days to suggest the due date.',
    },
    groups: {
      label: 'Groups',
      description: 'Umbrella terms for categories, e.g. structural fire protection.',
    },
    categories: {
      label: 'Categories',
      description: 'Classification of findings, each with a group.',
    },
    templates: {
      label: 'Defect catalog',
      description:
        'Templates with description, measure, legal basis and priority that findings start from.',
    },
  },
  catalogFields: {
    closed: 'Counts as done',
    level: 'Level',
    dueDays: 'Standard period in days',
    group: 'Group',
    category: 'Category',
    priority: 'Priority',
    description: 'Description',
    measure: 'Recommended measure',
    legalBasis: 'Legal basis',
    keywords: 'Keywords',
  },
  settings: {
    title: 'Statuses and priorities of findings',
    description:
      'Apply to all modules with findings. A change here also applies in the other modules.',
  },
};
